import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Checkbox, FormControlLabel, Tooltip, Typography, Chip,
} from '@mui/material'
import { Add, DeleteOutlined, Calculate } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  createOffer, updateOffer, getOffer, getAvailableStock,
  type OfferListItem, type SaveOfferPayload, type BelowCostViolationsResponse,
} from '../../api/oferte'
import { calculatePrice } from '../../api/preturi'
import { listClients, getClient, listSalesAgents, listClientUsualArticles } from '../../api/clienti'
import { listArticles } from '../../api/nomenclatoare'
import { listVatRates } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  articleId: z.string().nullable(),
  customDescription: z.string().optional(),
  requiresNewMold: z.boolean(),
  quantityThousands: z.string().min(1, 'Câmp obligatoriu'),
  pricePer1000: z.string().min(1, 'Câmp obligatoriu'),
  discountPercent: z.string().min(1, 'Câmp obligatoriu'),
  vatRateId: z.string().nullable(),
})
const schema = z.object({
  offerDate: z.string().min(1, 'Câmp obligatoriu'),
  clientId: z.string().min(1, 'Selectați un client'),
  contactId: z.string().nullable(),
  agentUserId: z.string().min(1, 'Selectați un agent'),
  segment: z.union([z.enum(['Lapte', 'Delicatese', 'Blistere', 'Altele']), z.literal('')]).optional(),
  validityDays: z.string().min(1, 'Câmp obligatoriu'),
  paymentTerms: z.string().optional(),
  estimatedDeliveryTerm: z.string().optional(),
  notes: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY_LINE = { articleId: null, customDescription: '', requiresNewMold: false, quantityThousands: '', pricePer1000: '', discountPercent: '0', vatRateId: null }
const EMPTY: FormValues = {
  offerDate: new Date().toISOString().slice(0, 10), clientId: '', contactId: null, agentUserId: '',
  segment: '', validityDays: '30', paymentTerms: '', estimatedDeliveryTerm: '', notes: '', lines: [],
}

/** FR-CER-007 — prefiltrare la crearea unei oferte din cererea sursă deja calificată. */
export interface OfferPrefill {
  clientId: string
  contactId: string | null
  estimatedDeliveryTerm: string
  lines: { articleId: string | null; customDescription: string; quantityThousands: string }[]
}

export default function OfferFormDialog({ target, sourceClientRequestId, prefill, onClose }: {
  target: OfferListItem | null | 'new'; sourceClientRequestId?: string | null; prefill?: OfferPrefill | null; onClose: () => void
}) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open })
  const { data: agents } = useQuery({ queryKey: ['comercial', 'agenti-vanzari'], queryFn: listSalesAgents, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'taxe', 'cote-tva'], queryFn: listVatRates, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['comercial', 'oferta', editing ? (target as OfferListItem).id : null],
    queryFn: () => getOffer((target as OfferListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const clientId = useWatch({ control, name: 'clientId' })
  const [belowCost, setBelowCost] = useState<BelowCostViolationsResponse | null>(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [autoTerm, setAutoTerm] = useState<string | null>(null)

  const { data: clientDetail } = useQuery({
    queryKey: ['comercial', 'client', clientId || null], queryFn: () => getClient(clientId), enabled: !!clientId,
  })
  // FR-MDC-007 — filtrare implicită (grupare, nu excludere) la alegerea articolului.
  const { data: usualArticles } = useQuery({
    queryKey: ['comercial', 'produse-uzuale', clientId || null], queryFn: () => listClientUsualArticles(clientId), enabled: !!clientId,
  })
  const usualArticleIds = new Set(usualArticles?.map((u) => u.articleId))

  useEffect(() => {
    if (editing && detail) {
      reset({
        offerDate: detail.offerDate, clientId: detail.clientId, contactId: detail.contactId, agentUserId: detail.agentUserId,
        segment: detail.segment ?? '', validityDays: String(detail.validityDays), paymentTerms: detail.paymentTerms ?? '',
        estimatedDeliveryTerm: detail.estimatedDeliveryTerm ?? '', notes: detail.notes ?? '',
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, customDescription: l.customDescription ?? '', requiresNewMold: l.requiresNewMold,
          quantityThousands: String(l.quantityThousands), pricePer1000: String(l.pricePer1000),
          discountPercent: String(l.discountPercent), vatRateId: l.vatRateId,
        })),
      })
    } else if (target === 'new') {
      reset(prefill ? {
        ...EMPTY, clientId: prefill.clientId, contactId: prefill.contactId,
        estimatedDeliveryTerm: prefill.estimatedDeliveryTerm,
        lines: prefill.lines.map((l) => ({ ...EMPTY_LINE, ...l })),
      } : EMPTY)
    }
    setAutoTerm(editing ? null : (prefill?.estimatedDeliveryTerm ?? null))
    setBelowCost(null)
    setOverrideReason('')
  }, [target, editing, detail, prefill, reset])

  const calcMutation = useMutation({
    mutationFn: (vars: { index: number; articleId: string; quantity: number }) => calculatePrice(clientId, vars.articleId, vars.quantity),
    onSuccess: (result, vars) => {
      if (result.netPrice != null) setValue(`lines.${vars.index}.pricePer1000`, String(result.netPrice.toFixed(4)))
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveOfferPayload = {
        offerDate: values.offerDate, clientId: values.clientId, contactId: values.contactId || null,
        agentUserId: values.agentUserId, segment: values.segment || null, validityDays: Number(values.validityDays),
        paymentTerms: values.paymentTerms || null, estimatedDeliveryTerm: values.estimatedDeliveryTerm || null,
        estimatedDeliveryTermOverridden: autoTerm !== null && values.estimatedDeliveryTerm !== autoTerm,
        notes: values.notes || null, sourceClientRequestId: editing ? null : (sourceClientRequestId ?? null),
        lines: values.lines.map((l) => ({
          articleId: l.articleId, customDescription: l.customDescription || null, requiresNewMold: l.requiresNewMold,
          quantityThousands: Number(l.quantityThousands), pricePer1000: Number(l.pricePer1000),
          discountPercent: Number(l.discountPercent), vatRateId: l.vatRateId,
        })),
        belowCostOverrideReason: overrideReason || null,
      }
      if (editing) return updateOffer((target as OfferListItem).id, payload)
      return createOffer(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'oferte'] })
      onClose()
    },
    onError: (e) => {
      if (axios.isAxiosError(e) && e.response?.status === 409 && e.response.data?.violations) {
        setBelowCost(e.response.data as BelowCostViolationsResponse)
      } else {
        setBelowCost(null)
      }
    },
  })

  const genericError = mutation.isError && !belowCost ? errorMessage(mutation.error) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? `Editare ofertă ${(target as OfferListItem).offerNumber}` : 'Ofertă nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="offerDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data ofertei" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.offerDate} helperText={errors.offerDate?.message} />
              )} />
              <Controller name="clientId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={clients?.map((c) => c.id) ?? []}
                  getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Client" error={!!errors.clientId} helperText={errors.clientId?.message} />}
                />
              )} />
              <Controller name="contactId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ width: 220 }} options={clientDetail?.contacts.map((c) => c.id!) ?? []}
                  getOptionLabel={(id) => clientDetail?.contacts.find((c) => c.id === id)?.name ?? id}
                  value={field.value} onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => <TextField {...params} label="Persoană contact" />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="agentUserId" control={control} render={({ field }) => (
                <TextField {...field} select label="Agent" sx={{ width: 220 }} error={!!errors.agentUserId} helperText={errors.agentUserId?.message}>
                  {agents?.map((a) => <MenuItem key={a.id} value={a.id}>{a.fullName}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="segment" control={control} render={({ field }) => (
                <TextField {...field} select label="Segment" sx={{ width: 160 }}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="Lapte">Lapte</MenuItem>
                  <MenuItem value="Delicatese">Delicatese</MenuItem>
                  <MenuItem value="Blistere">Blistere</MenuItem>
                  <MenuItem value="Altele">Altele</MenuItem>
                </TextField>
              )} />
              <Controller name="validityDays" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Valabilitate (zile)" sx={{ width: 160 }}
                  error={!!errors.validityDays} helperText={errors.validityDays?.message} />
              )} />
              <Controller name="paymentTerms" control={control} render={({ field }) => (
                <TextField {...field} label="Termeni de plată" fullWidth />
              )} />
            </Stack>
            <Controller name="estimatedDeliveryTerm" control={control} render={({ field }) => (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                <TextField {...field} fullWidth multiline={autoTerm !== null} minRows={autoTerm !== null ? 2 : 1}
                  label={autoTerm !== null ? 'Termen estimat de livrare (din estimarea ATP a cererii)' : 'Termen estimat de livrare (manual)'}
                  helperText={autoTerm !== null && field.value !== autoTerm ? 'Modificat față de estimarea automată' : undefined}
                />
                {editing && detail?.estimatedDeliveryTermOverridden && (
                  <Chip size="small" sx={{ mt: 1 }} label="Suprascris manual" color="warning" variant="outlined" />
                )}
              </Stack>
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <ArticleAwareLine key={f.id} index={i} control={control} errors={errors} articles={articles}
                vatRates={vatRates} clientId={clientId} usualArticleIds={usualArticleIds} onRemove={() => lines.remove(i)}
                onCalculate={(articleId: string, quantity: number) => calcMutation.mutate({ index: i, articleId, quantity })}
                calculating={calcMutation.isPending && calcMutation.variables?.index === i} />
            ))}
            <Box>
              <Button size="small" startIcon={<Add />} onClick={() => lines.append(EMPTY_LINE)}>Adaugă linie</Button>
            </Box>

            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Observații" fullWidth multiline minRows={2} />
            )} />

            {belowCost && (
              <>
                <Alert severity="warning">
                  {belowCost.violations.map((v) => (
                    <div key={v.articleCode}>{v.articleCode}: preț {v.pricePer1000.toFixed(4).replace('.', ',')} sub costul standard {v.standardCost.toFixed(4).replace('.', ',')}</div>
                  ))}
                </Alert>
                <TextField label="Motiv suprascriere preț sub cost" fullWidth multiline minRows={2}
                  value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              </>
            )}
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {genericError && <Alert severity="error">{genericError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || (!!belowCost && !overrideReason.trim())}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {belowCost ? 'Salvează cu suprascriere' : 'Salvează'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ArticleAwareLine({ index, control, errors, articles, vatRates, clientId, usualArticleIds, onRemove, onCalculate, calculating }: any) {
  const articleId = useWatch({ control, name: `lines.${index}.articleId` })
  const quantity = useWatch({ control, name: `lines.${index}.quantityThousands` })
  const sortedArticles = articles
    ? [...articles].sort((a: { id: string }, b: { id: string }) => Number(usualArticleIds?.has(b.id)) - Number(usualArticleIds?.has(a.id)))
    : []

  // FR-OFR-009 — informativ, nu blocant: agentul vede disponibilul real când pune preț.
  const { data: available } = useQuery({
    queryKey: ['comercial', 'oferte', 'disponibil-stoc', articleId], queryFn: () => getAvailableStock(articleId), enabled: !!articleId,
  })

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Stack sx={{ flexGrow: 1.2 }}>
        <Controller name={`lines.${index}.articleId`} control={control} render={({ field }) => (
          <Autocomplete
            fullWidth options={sortedArticles.map((a: { id: string }) => a.id)}
            groupBy={(id) => usualArticleIds?.has(id) ? 'Produse uzuale' : 'Tot catalogul'}
            getOptionLabel={(id) => { const a = articles?.find((x: { id: string }) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
            value={field.value} onChange={(_, v) => field.onChange(v)}
            renderInput={(params) => <TextField {...params} label="Articol (gol = produs custom)" />}
          />
        )} />
        {articleId && available !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>Stoc disponibil: {available}</Typography>
        )}
      </Stack>
      {!articleId && (
        <>
          <Controller name={`lines.${index}.customDescription`} control={control} render={({ field }) => (
            <TextField {...field} label="Descriere custom" sx={{ flexGrow: 1 }} />
          )} />
          <Controller name={`lines.${index}.requiresNewMold`} control={control} render={({ field }) => (
            <FormControlLabel sx={{ whiteSpace: 'nowrap' }} control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Matriță nouă" />
          )} />
        </>
      )}
      <Controller name={`lines.${index}.quantityThousands`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Cant. (mii)" sx={{ width: 110 }}
          error={!!errors.lines?.[index]?.quantityThousands} helperText={errors.lines?.[index]?.quantityThousands?.message} />
      )} />
      <Controller name={`lines.${index}.pricePer1000`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Preț/1000" sx={{ width: 120 }}
          error={!!errors.lines?.[index]?.pricePer1000} helperText={errors.lines?.[index]?.pricePer1000?.message} />
      )} />
      <Tooltip title={!clientId || !articleId || !quantity ? 'Selectați client, articol și cantitate' : 'Calculează preț din motorul de prețuri'}>
        <span>
          <IconButton size="small" disabled={!clientId || !articleId || !quantity || calculating} sx={{ mt: 0.5 }}
            onClick={() => onCalculate(articleId, Number(quantity))}>
            {calculating ? <CircularProgress size={16} /> : <Calculate fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Controller name={`lines.${index}.discountPercent`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Discount %" sx={{ width: 100 }}
          error={!!errors.lines?.[index]?.discountPercent} helperText={errors.lines?.[index]?.discountPercent?.message} />
      )} />
      <Controller name={`lines.${index}.vatRateId`} control={control} render={({ field }) => (
        <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} select label="TVA" sx={{ width: 110 }}>
          <MenuItem value="">—</MenuItem>
          {vatRates?.map((v: { id: string; code: string; ratePercent: number }) => <MenuItem key={v.id} value={v.id}>{v.code} ({v.ratePercent}%)</MenuItem>)}
        </TextField>
      )} />
      <IconButton onClick={onRemove} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
    </Stack>
  )
}
